import { Select, SelectItem, SelectProps } from '@nextui-org/react'
import { forwardRef } from 'react'
import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type DropdownProps = Omit<SelectProps, 'children' | 'onChange'> & {
    options: { value: string; label: string }[]
    selectionMode: 'none' | 'single' | 'multiple'
    value: string | string[] | undefined
    className: string
    onChange: (value: string | string[]) => void
    error?: FieldError | undefined
}
export const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>(
    (
        {
            placeholder,
            options,
            value,
            onChange,
            error,
            selectionMode,
            className,
            ...props
        },
        ref
    ) => {
        const { t } = useTranslation()

        return (
            <Select
                ref={ref}
                variant="bordered"
                aria-label={placeholder || 'selectOption'}
                placeholder={placeholder}
                selectionMode={selectionMode}
                selectedKeys={(() => {
                    if (selectionMode === 'multiple') {
                        return Array.isArray(value)
                            ? new Set(value.map((v) => (v ?? '').toString()))
                            : new Set([])
                    }
                    // single mode: allow empty string as a valid key
                    if (value === undefined) {
                        return new Set([])
                    }

                    return new Set([value.toString()])
                })()}
                isInvalid={error ? true : false}
                errorMessage={error ? `${error.message}*` : ''}
                onSelectionChange={(keys) => {
                    const selectedValues = Array.from(keys).map((key) =>
                        key.toString()
                    )

                    if (selectionMode === 'multiple') {
                        onChange(selectedValues)
                    } else {
                        onChange(selectedValues[0] || '')
                    }
                }}
                className={`max-w-80 ${className} md:max-w-full`}
                classNames={{
                    selectorIcon: `text-primary dark:text-primary-dark ${error ? 'text-danger dark:text-danger' : ''}`,
                    listbox: 'max-h-60 overflow-y-auto scrollbar-custom',
                    ...props.classNames // Permitir sobrescribir classNames por defecto
                }}
                onOpenChange={(open) => {
                    if (props.onOpenChange) {
                        props.onOpenChange(open)
                    }
                }}
                {...props}
            >
                {options.map((option) => {
                    // Asegurar que tenemos valores válidos para mostrar
                    const displayText =
                        option.label || option.value || 'Unknown'
                    const textValue = String(displayText)

                    return (
                        <SelectItem
                            key={option.value.toString()}
                            value={option.value.toString()}
                            textValue={textValue}
                            title={displayText}
                            className="cursor-pointer"
                        >
                            {displayText}
                        </SelectItem>
                    )
                })}
            </Select>
        )
    }
)
// Add display name for better debugging
Dropdown.displayName = 'Dropdown'
